import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";

const FORM_NAME = "SBI_ID";
const PDF_ENDPOINT = "/FillMyForm/SBI_ID/Print.php";

const fields = [
  { name: "aadhaarno", label: "Aadhaar Number", maxLength: 12, numeric: true },
  { name: "cifno", label: "CIF Number", maxLength: 11, numeric: true },
  { name: "acno", label: "Account Number", maxLength: 11, numeric: true, required: true },
  { name: "ifsc", label: "IFSC Code", maxLength: 11, uppercase: true, required: true },
  { name: "firstname", label: "First Name", maxLength: 20, alphaOnly: true, required: true },
  { name: "middlename", label: "Middle Name", maxLength: 20, alphaOnly: true },
  { name: "lastname", label: "Last Name", maxLength: 20, alphaOnly: true, required: true },
  { name: "add1", label: "Address Line 1", maxLength: 25, required: true },
  { name: "add2", label: "Address Line 2", maxLength: 25, required: true },
  { name: "village", label: "Village", maxLength: 20, required: true },
  { name: "city", label: "City", maxLength: 20, required: true },
  { name: "pin", label: "PIN", maxLength: 6, numeric: true, required: true },
  { name: "koname", label: "KO Name", maxLength: 20, alphaOnly: true },
  { name: "kolocation", label: "KO Location", maxLength: 20 },
];

const tabs = ["Applicant Details", "Preview"];
const initialFormData = Object.fromEntries(fields.map((field) => [field.name, ""]));

async function readJson(response) {
  const text = await response.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text);
  } catch {
    throw new Error("The server returned an invalid JSON response.");
  }
}

export default function SBI_ID() {
  const [formData, setFormData] = useState(initialFormData);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [errors, setErrors] = useState({});
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);

  useEffect(() => {
    if (currentTab !== 0) return undefined;
    const frame = window.requestAnimationFrame(() => {
      document.getElementById("image")?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [currentTab]);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent(FORM_NAME)}`,
          {
            credentials: "include",
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          },
        );

        const result = await readJson(response);
        if (!response.ok || !result?.success || !result?.found || !result?.data) return;

        setFormData((previous) => ({
          ...previous,
          city: previous.city || result.data.city || "",
          pin: previous.pin || result.data.pin || "",
        }));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("SBI_ID autofill error:", error);
        }
      }
    })();

    return () => controller.abort();
  }, []);

  useEffect(
    () => () => {
      if (imagePreview?.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
    },
    [imagePreview],
  );

  useEffect(
    () => () => {
      if (pdfUrl?.startsWith("blob:")) URL.revokeObjectURL(pdfUrl);
    },
    [pdfUrl],
  );

  const updateField = (field, rawValue) => {
    let value = rawValue;
    if (field.numeric) value = value.replace(/\D/g, "");
    if (field.alphaOnly) value = value.replace(/[^A-Za-z ]/g, "");
    if (field.uppercase) value = value.toUpperCase();

    setFormData((previous) => ({ ...previous, [field.name]: value }));
    setErrors((previous) => ({ ...previous, [field.name]: "" }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    setImageFile(file);
    setErrors((previous) => ({ ...previous, image: "" }));

    setImagePreview((previous) => {
      if (previous?.startsWith("blob:")) URL.revokeObjectURL(previous);
      return file ? URL.createObjectURL(file) : "";
    });
  };

  const validateField = (field) => {
    const value = String(formData[field.name] ?? "").trim();

    if (field.required && !value) return `${field.label} is required.`;

    if (field.alphaOnly && value && !/^[A-Za-z ]+$/.test(value)) {
      return `${field.label} must contain alphabet letters only.`;
    }

    if (field.name === "aadhaarno" && value && !/^\d{12}$/.test(value)) {
      return "Aadhaar number must contain exactly 12 digits.";
    }

    if (field.name === "cifno" && value && !/^\d{11}$/.test(value)) {
      return "CIF number must contain 11 digits.";
    }

    if (field.name === "acno" && value && !/^\d{11}$/.test(value)) {
      return "Account number must contain 11 digits.";
    }

    if (field.name === "pin" && value && !/^\d{6}$/.test(value)) {
      return "PIN must contain exactly 6 digits.";
    }

    if (field.name === "ifsc" && value && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) {
      return "Enter a valid IFSC code.";
    }

    return "";
  };

  const validateAll = () => {
    const nextErrors = {};
    if (!imageFile) nextErrors.image = "Please choose an image.";

    fields.forEach((field) => {
      const message = validateField(field);
      if (message) nextErrors[field.name] = message;
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = () => {
    if (!validateAll()) return;
    setCurrentTab(1);
    setMaxAllowedTab(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrevious = () => {
    setCurrentTab(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLastFieldKeyDown = (event, fieldName) => {
    const lastFieldName = fields[fields.length - 1].name;
    if (fieldName === lastFieldName && event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      nextButtonRef.current?.focus();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateAll()) {
      setCurrentTab(0);
      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const body = new FormData();
      body.append("image", imageFile);
      Object.entries(formData).forEach(([name, value]) => {
        body.append(name, String(value ?? ""));
      });

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 60000);
      let response;

      try {
        response = await fetch(PDF_ENDPOINT, {
          method: "POST",
          body,
          credentials: "include",
          headers: { Accept: "application/pdf" },
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }

      if (!response.ok) {
        throw new Error((await response.text()) || `PDF generation failed (${response.status}).`);
      }

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/pdf")) {
        throw new Error((await response.text()) || "Print.php did not return a PDF file.");
      }

      const objectUrl = URL.createObjectURL(await response.blob());
      setPdfUrl((previous) => {
        if (previous?.startsWith("blob:")) URL.revokeObjectURL(previous);
        return objectUrl;
      });
      setShowPdf(true);
    } catch (error) {
      setPdfError(
        error.name === "AbortError"
          ? "PDF generation timed out."
          : error.message || "Unable to generate PDF preview.",
      );
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.name];

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>
          {field.label}
          {field.required && <span className="text-red-500"> *</span>}
        </Label>

        <Input
          id={field.name}
          name={field.name}
          type="text"
          value={formData[field.name]}
          maxLength={field.maxLength}
          placeholder={field.label}
          className={error ? "border-red-500" : ""}
          onChange={(event) => updateField(field, event.target.value)}
          onKeyDown={(event) => handleLastFieldKeyDown(event, field.name)}
        />

        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  if (showPdf && pdfUrl) {
    return (
      <PdfViewerContainer
        pdfUrl={pdfUrl}
        title="SBI Kiosk ID Card"
        onBack={() => setShowPdf(false)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">🪪 SBI Kiosk ID Card</CardTitle>
            <p className="text-muted-foreground">
              Upload the applicant photograph and enter the ID-card details carefully.
            </p>
          </CardHeader>
        </Card>

        <div className="mb-8 grid grid-cols-2 gap-3">
          {tabs.map((tab, index) => (
            <Button
              key={tab}
              type="button"
              variant={currentTab === index ? "default" : "outline"}
              disabled={index > maxAllowedTab}
              onClick={() => setCurrentTab(index)}
            >
              {index + 1}. {tab}
            </Button>
          ))}
        </div>

        {currentTab === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>👤 Applicant Details</CardTitle>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_180px]">
                <div className="space-y-2">
                  <Label htmlFor="image">
                    Applicant Photograph<span className="text-red-500"> *</span>
                  </Label>

                  <Input
                    id="image"
                    name="image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className={errors.image ? "border-red-500" : ""}
                  />

                  {errors.image && <p className="text-sm text-red-500">{errors.image}</p>}
                </div>

                <div className="flex min-h-[180px] items-center justify-center overflow-hidden rounded-lg border bg-muted/30">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Applicant preview"
                      className="h-[170px] w-[150px] object-cover"
                    />
                  ) : (
                    <span className="px-4 text-center text-sm text-muted-foreground">
                      Image preview
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {fields.map(renderField)}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>📋 Preview</CardTitle>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="flex justify-center">
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Applicant"
                    className="h-[170px] w-[150px] rounded-lg border object-cover"
                  />
                )}
              </div>

              <div className="overflow-hidden rounded-lg border">
                {fields.map((field) => (
                  <div
                    key={field.name}
                    className="grid grid-cols-2 border-b p-3 last:border-b-0"
                  >
                    <span className="font-medium">{field.label}</span>
                    <span className="break-words">{formData[field.name] || "-"}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {pdfError && (
          <p className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-red-700">
            {pdfError}
          </p>
        )}

        <div className="mt-6 flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={goPrevious}
            disabled={currentTab === 0}
          >
            ← Previous
          </Button>

          {currentTab === 1 ? (
            <Button
              type="button"
              ref={nextButtonRef}
              onClick={handleSubmit}
              disabled={pdfLoading}
            >
              {pdfLoading ? "Generating PDF..." : "Submit & Open PDF"}
            </Button>
          ) : (
            <Button type="button" ref={nextButtonRef} onClick={goNext}>
              Preview
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
